// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck ignoring this files for now because there are just too many errors to handle
import { devtoolsExchange } from "@urql/devtools";
import { GraphQLError, OperationDefinitionNode } from "graphql";
import { Client, createClient, fetchExchange, mapExchange, gql } from "urql";
import { cacheExchange } from "@urql/exchange-graphcache";
import { SuggestedBatchesQuery } from "./generated/graphql";
import schema, { GraphCacheConfig, SuggestedBatchesDocument } from "./generated/graphql";

import {
  InvalidateCachedMentees,
  InvalidateCachedMentors,
  InvalidateCachedSavedMatches,
  RemoveFromCachedSavedMatches,
  RemoveMenteeFromCachedMenteeList,
} from "./matching_dashboard/cache_exchange";
import { apm } from "@elastic/apm-rum";
import { matchPath } from "react-router-dom";
import { captureError } from "../utils/capture_error";

// see the cacheExchange below - this is used to manually update the urql cache when we save a new
// survey response https://formidable.com/open-source/urql/docs/graphcache/custom-updates/#cacheupdatequery
const surveyResponseQuery = gql`
  query CachedParticipantSurveyResponses(
    $participantId: ID!
    $surveyId: ID!
    $context: SurveyResponseContextFilter
  ) {
    participant(id: $participantId) {
      id
      surveyResponses(surveyId: $surveyId, context: $context) {
        id
      }
    }
  }
`;

export default function useUrqlClient({ csrfToken }: { csrfToken?: string | null }): Client {
  return createClient({
    url: "/graphql",
    fetchOptions: csrfToken
      ? {
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        }
      : {},
    exchanges: [
      devtoolsExchange,
      cacheExchange<GraphCacheConfig>({
        // https://github.com/FormidableLabs/urql/discussions/1389#discussioncomment-367599
        schema: schema,
        // these object types shouldn't be normalized, they should instead be embedded directly in their parent object
        // in that case they won't have id fields
        // https://formidable.com/open-source/urql/docs/graphcache/errors/#15-invalid-key
        keys: {
          AffectedObjectTypeCount: () => null,
          MessageTemplateTagCount: () => null,
          UserStateVariable: () => null,
          SuggestedBatchSettings: () => null,
          SingleSelectHardMatchQuestion: () => null,
          MultiSelectHardMatchQuestion: () => null,
          MatchingCriteria: () => null,
          AutoMatchSettings: () => null,
          GroupMatchingSettings: () => null,
          SatisfactionReport: () => null,
          EngagementBenchmarkData: () => null,
          SatisfactionAverage: () => null,
          QueryableMetrics: () => null,
          Interval: () => null,
          ContactsMetrics: () => null,
          ParticipantsMetrics: () => null,
          MentorshipsMetrics: () => null,
          FlagsMetrics: () => null,
          UsersMetrics: () => null,
          CsvExports: () => null,
          PartnerDashboardExports: () => null,
          TopicCollection: () => null,
          UserProfileData: (attr) =>
            attr.profileAttribute && attr.values
              ? attr.profileAttribute.id + "-" + attr.values.map((v) => v.id).join("")
              : null,
          ProgramProfileAttributeDefaultSettings: () => null,
          ProgramProfileAttributeCategoryResult: () => null,
          RecruitmentOpportunitiesMetricsResult: () => null,
          StudentGoalsList: () => null,
          SurveyQuestion: (attr) => {
            if (attr.customizations) {
              // In certain instances, we pass the argument 'surveyCustomRenderOverride' to the 'questions' field on surveys. This argument actively modifies the output value of the 'customizations' field within the survey.
              // However, due to GraphQL recognizing 'customizations' as a 'JSON' type, there exists no intrinsic way for GraphQL to understand that this value should not be cached or normalized based on the question ID.
              // Essentially, we're using an argument to change the output value of an entity, leading to conflicts in caching.
              // To mitigate this issue, we're converting 'customizations' into a string and hashing it. This process ensures unique treatment for each question based on their 'customizations', allowing us to continue utilizing the cache and reducing the complexity of request policies or refetching.
              // It's important to note that this solution is temporary until we devise a more effective method of managing 'customizations' and the normalization of survey question types.

              const customizations = JSON.stringify(attr.customizations);
              const hash = customizations.split("").reduce(function (a, b) {
                a = (a << 5) - a + b.charCodeAt(0);
                return a & a;
              }, 0);

              return attr.id + "-" + hash;
            }

            return attr.id || null;
          },
        },
        updates: {
          Mutation: {
            duplicateMessageTemplates: (_result, _args, cache) => {
              const templateQueries = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "messageTemplates");

              templateQueries.forEach(({ fieldName, arguments: variables }) => {
                cache.invalidate("Query", fieldName, variables ?? undefined);
              });
            },
            createMessageTemplateTag: (_result, _args, cache) => {
              const tagQueries = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "messageTemplateTags");

              tagQueries.forEach(({ fieldName, arguments: variables }) => {
                cache.invalidate("Query", fieldName, variables ?? undefined);
              });
            },
            createMatchingSurvey: (result, _args, cache) => {
              if (!result?.createMatchingSurvey) return;
              const surveysTaskQueries = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "surveys");

              surveysTaskQueries.forEach(({ fieldName, arguments: variables }) => {
                cache.invalidate("Query", fieldName, variables ?? undefined);
              });
            },
            createProgramTask: (_result, _args, cache) => {
              const programTaskQueries = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "programTasks");

              programTaskQueries.forEach(({ fieldName, arguments: variables }) => {
                if (variables) {
                  cache.invalidate("Query", fieldName, variables);
                }
              });
            },
            createMicroSurvey: (_result, _args, cache) => {
              const microSurveyQueries = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "microSurveys");

              microSurveyQueries.forEach(({ fieldName, arguments: variables }) => {
                if (variables) {
                  cache.invalidate("Query", fieldName, variables);
                }
              });
            },
            createSchool: (_result, _args, cache) => {
              const schoolQueries = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "schools");

              schoolQueries.forEach(({ fieldName, arguments: variables }) => {
                if (variables) {
                  cache.invalidate("Query", fieldName, variables);
                }
              });
            },
            createProgram: (_result, _args, cache) => {
              const programQueries = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "programs");

              programQueries.forEach(({ fieldName, arguments: variables }) => {
                if (variables) {
                  cache.invalidate("Query", fieldName, variables);
                }
              });
            },
            saveSurveyResponse: (result, args, cache) => {
              const context = result.saveSurveyResponse?.response?.context;

              // Invalidate any task assignments as completing this survey may have
              // completed a task
              cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "taskAssignments")
                .forEach(({ fieldName, arguments: variables }) => {
                  if (variables) {
                    cache.invalidate("Query", fieldName, variables);
                  }
                });

              cache.updateQuery(
                {
                  query: surveyResponseQuery,
                  variables: {
                    userId: args.response.userId,
                    surveyId: args.response.surveyId,
                  },
                },
                (data) => {
                  if (data && result.saveSurveyResponse?.response) {
                    data.participant.surveyResponses.push(result.saveSurveyResponse.response);
                  }
                  return data;
                },
              );
              // if the survey response had an associated context, also manually update a query where both
              // survey id and context are specified (this is particularly important for "mentorship_id")
              if (context) {
                cache.updateQuery(
                  {
                    query: surveyResponseQuery,
                    variables: {
                      userId: args.response.userId,
                      surveyId: args.response.surveyId,
                      context: context,
                    },
                  },
                  (data) => {
                    if (data && result.saveSurveyResponse?.response) {
                      data.participant.surveyResponses.push(result.saveSurveyResponse.response);
                    }
                    return data;
                  },
                );
              }
            },
            autoSuggestMatches: (_result, _args, cache) => {
              const suggestedBatches = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "suggestedBatches");
              suggestedBatches.forEach(({ fieldName, arguments: variables }) => {
                cache.invalidate("Query", fieldName, variables ?? undefined);
              });
            },
            deleteSuggestedBatch: (_result, args, cache) => {
              const suggestedBatches = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "suggestedBatches");

              suggestedBatches.forEach(({ arguments: variables }) => {
                cache.updateQuery(
                  {
                    query: SuggestedBatchesDocument,
                    variables: {
                      // @TODO Leaving for now - this was existing code that seems to have gotten a stricter type-check when refactoring to
                      // use the latest codegen packages
                      // eslint-disable-next-line
                      // @ts-ignore
                      filter: variables.filter,
                    },
                  },
                  (data: SuggestedBatchesQuery | null) => {
                    if (data?.suggestedBatches?.nodes) {
                      data.suggestedBatches.nodes = data.suggestedBatches.nodes.filter(
                        (suggestedBatch) => suggestedBatch.id !== args.id,
                      );
                      data.suggestedBatches.totalCount = data.suggestedBatches.nodes.length;
                    }
                    return data;
                  },
                );
              });
            },
            createSavedMatch: (result, args, cache) => {
              InvalidateCachedSavedMatches(cache);

              // We don't want to remove any mentees if errors happen.
              if (result.createSavedMatch?.errors?.length) {
                return;
              }
              RemoveMenteeFromCachedMenteeList(args.studentId, cache);
            },
            deleteSavedMatches: (_result, _args, cache) => {
              InvalidateCachedSavedMatches(cache);
              InvalidateCachedMentees(cache);
              InvalidateCachedMentors(cache);
            },
            makeMatch: (result, args, cache) => {
              // If we get an error or a rematch confirmation, don't remove the selected participants.
              if (Array.isArray(result?.makeMatch?.errors) && result?.makeMatch?.errors.length) {
                return;
              }
              if (result?.makeMatch?.showAllowRematchConfirm) {
                return;
              }
              RemoveMenteeFromCachedMenteeList(args.studentId, cache);
              InvalidateCachedSavedMatches(cache);
            },
            makeSavedGroupMatches: (result, args, cache) => {
              // We don't want to remove any mentees if errors happen.
              if (
                result?.makeSavedGroupMatches.byFilter?.errors.length ||
                result?.makeSavedGroupMatches.byIds?.errors.length
              ) {
                return;
              }
              if (result?.makeSavedGroupMatches?.byIds?.savedMatchIds) {
                RemoveFromCachedSavedMatches(
                  result.makeSavedGroupMatches.byIds.savedMatchIds,
                  cache,
                );
              } else if (result?.makeSavedGroupMatches?.byFilter?.savedMatchIds) {
                RemoveFromCachedSavedMatches(
                  result.makeSavedGroupMatches.byFilter.savedMatchIds,
                  cache,
                );
              }
            },
            makeSavedMatches: (result, args, cache) => {
              // We don't want to remove any mentees if errors happen.
              if (
                result?.makeSavedMatches.byIds?.errors.length ||
                result?.makeSavedMatches.byFilter?.errors.length
              ) {
                return;
              }
              if (result?.makeSavedMatches?.byIds?.savedMatchIds) {
                RemoveFromCachedSavedMatches(result.makeSavedMatches.byIds.savedMatchIds, cache);
              } else if (result?.makeSavedMatches?.byFilter?.savedMatchIds) {
                RemoveFromCachedSavedMatches(result.makeSavedMatches.byFilter.savedMatchIds, cache);
              }
            },
            createFlagContact: (result, _args, cache) => {
              if (!result?.createFlagContact) return;
              const flagContactsQueries = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "flagContacts");

              flagContactsQueries.forEach(({ fieldName, arguments: variables }) => {
                cache.invalidate("Query", fieldName, variables ?? undefined);
              });
            },
            updateFlag: (result, args, cache) => {
              const flagsForQueries = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "flags");
              flagsForQueries.forEach(({ fieldName, arguments: variables }) => {
                cache.invalidate("Query", fieldName, variables ?? undefined);
              });
            },
            deleteProgramContacts: (result, _args, cache) => {
              if (!result?.deleteProgramContacts) return;
              const programQueries = cache
                .inspectFields("Query")
                .filter((f) => f.fieldName === "program");

              programQueries.forEach(({ fieldName, arguments: variables }) => {
                cache.invalidate("Query", fieldName, variables);
              });
            },
            deregisterParticipant: (result, _args, cache) => {
              if (result?.deregisterParticipant?.errors?.length) return;

              const userQueries = cache
                .inspectFields("Query")
                .filter(
                  (f) =>
                    f.fieldName === "user" &&
                    result?.deregisterParticipant?.participant?.user.id &&
                    f.arguments?.id === result.deregisterParticipant.participant.user.id,
                );
              userQueries.forEach(({ fieldName, arguments: variables }) => {
                cache.invalidate("Query", fieldName, variables);
              });
            },
          },
        },
      }),
      mapExchange({
        onError: async (error, operation) => {
          /*
           * Special case to handle SSO users that have an expired token
           * This is mainly a place holder/directional until we get a program
           * with custom SSO implemented. At that point, we'll need to think about
           * UI/UX here. A user should be notified that their session is expired and
           * they'll need to log in again either by pop up before redirecting or
           * flash message after being redirected
           *
           * See graphql_controller.rb#handle_expired_sso_session!
           */
          const ssoError = (err: GraphQLError): boolean =>
            err?.extensions?.code === "SSO_TOKEN_EXPIRED";
          const didSsoError = error.graphQLErrors.some(ssoError);
          if (didSsoError) {
            const err = error.graphQLErrors.find(ssoError);
            const path = err?.extensions?.redirectTo || "/sign_in";
            /*
             * When we work on the UX for this, I imagine passing the error and previous URL along
             * as query params. Then have the sign in page trigger an alert with the query param
             * error and redirect back to previous page upon successful sign in.
             */
            window.location.replace(path as string);
            return;
          }

          // handle user being logged out
          if (error?.response?.status === 401) {
            const signinPath = window.location.pathname.includes("admin")
              ? "/admin"
              : "" + "/sign_in";
            window.location.replace(signinPath);
            return;
          }

          // TODO: This should be replaced if we decide to log errors for all graphql failures or updated when the old partner dashboard is removed
          if (window.location.pathname.includes("/partner_dashboard/new/programs/") && true) {
            const pathParams = matchPath(
              "/partner_dashboard/new/programs/:programId/*",
              window.location.pathname,
            )?.params ?? { programId: "" };
            const graphQLErrorsForAPM = error?.graphQLErrors?.join("; ") ?? "";
            const graphQLQueries = operation.query.definitions
              .map((definition) => {
                const def = definition as OperationDefinitionNode;
                return def?.name?.value;
              })
              .join(", ");

            const programId = operation.variables?.programId ?? "";
            const userId = operation.variables?.userId ?? "";

            apm.addLabels({ program_id: pathParams.programId ?? "" });

            // interrogate other error properties for more info
            const errorMessage = error?.message ?? "";
            const networkErrorMessage = error?.networkError?.message ?? "";

            captureError(
              new Error(
                `Error with queries ${graphQLQueries}: ${
                  graphQLErrorsForAPM || errorMessage || networkErrorMessage
                }${programId && ` {program: ${programId}}`}${userId && ` {userId: ${userId}}`}`,
              ),
            );
          }
        },
      }),
      fetchExchange,
    ],
  });
}
